// Score / progression state, persisted across sessions.
// Drives the live HUD on the map, the tier-on-track indicator, and the
// final ceremony reveal.

const KEY = 'pa-score-v1';

const GIVE_UP_PENALTY = 3;
const TIER_GOLD_GUESSES = 30;
const TIER_GOLD_TIME_MS = 10 * 60 * 1000;
const TIER_SILVER_GUESSES = 36;

export type Tier = 'gold' | 'silver' | 'bronze';

export interface ScoreState {
  // Guesses submitted per card (the winning guess counts as 1, give-up = 0
  // submitted guesses + the penalty added at totals time).
  guessesByCard: Record<string, number>;
  gaveUpByCard: Record<string, boolean>;
  startedAt: number | null;
  finishedAt: number | null;
}

function emptyScore(): ScoreState {
  return { guessesByCard: {}, gaveUpByCard: {}, startedAt: null, finishedAt: null };
}

export function loadScore(): ScoreState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ScoreState;
      return {
        guessesByCard: parsed.guessesByCard ?? {},
        gaveUpByCard: parsed.gaveUpByCard ?? {},
        startedAt: parsed.startedAt ?? null,
        finishedAt: parsed.finishedAt ?? null,
      };
    }
  } catch {}
  return emptyScore();
}

export function saveScore(s: ScoreState): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {}
}

export function resetScore(): void {
  try { localStorage.removeItem(KEY); } catch {}
}

/** Counts the winning guess. e.g. solved on 1st try -> 1. */
export function recordResolve(
  cardId: string,
  guesses: number,
  gaveUp: boolean,
): ScoreState {
  const s = loadScore();
  if (!s.startedAt) s.startedAt = Date.now();
  // Don't downgrade a previous win to a gave-up (replays).
  const previouslyWon = s.guessesByCard[cardId] != null && !s.gaveUpByCard[cardId];
  if (previouslyWon && gaveUp) {
    saveScore(s);
    return s;
  }
  s.guessesByCard[cardId] = guesses;
  s.gaveUpByCard[cardId] = gaveUp;
  saveScore(s);
  return s;
}

export function markFinishedIfDone(totalCards: number): ScoreState {
  const s = loadScore();
  const solved = Object.keys(s.guessesByCard).length;
  if (solved >= totalCards && !s.finishedAt) {
    s.finishedAt = Date.now();
    saveScore(s);
  }
  return s;
}

export function totalGuesses(s: ScoreState): number {
  let total = 0;
  for (const id of Object.keys(s.guessesByCard)) {
    total += s.guessesByCard[id] ?? 0;
    if (s.gaveUpByCard[id]) total += GIVE_UP_PENALTY;
  }
  return total;
}

export function elapsedMs(s: ScoreState): number {
  if (!s.startedAt) return 0;
  const end = s.finishedAt ?? Date.now();
  return Math.max(0, end - s.startedAt);
}

export function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export function solvedCount(s: ScoreState): number {
  return Object.keys(s.guessesByCard).length;
}

/**
 * Cards the player actually SOLVED (excluding give-ups). This is what
 * counts toward monument unlocks — revealing the answer fills your
 * cabinet for browsing but doesn't earn you a monument tick.
 */
export function winCount(s: ScoreState): number {
  return Object.keys(s.guessesByCard).filter((id) => !s.gaveUpByCard[id]).length;
}

/**
 * Tier the player is currently on track for. While in progress, returns the
 * BEST tier that's still achievable given current guess + time spend.
 * On completion, returns the tier actually earned.
 */
export function tierOnTrack(s: ScoreState, totalCards: number): Tier {
  const used = totalGuesses(s);
  const elapsed = elapsedMs(s);
  const done = solvedCount(s) >= totalCards;

  if (done) {
    if (used <= TIER_GOLD_GUESSES && elapsed <= TIER_GOLD_TIME_MS) return 'gold';
    if (used <= TIER_SILVER_GUESSES) return 'silver';
    return 'bronze';
  }
  // In progress — has the player already exceeded a tier's threshold?
  if (used > TIER_SILVER_GUESSES) return 'bronze';
  if (used > TIER_GOLD_GUESSES || elapsed > TIER_GOLD_TIME_MS) return 'silver';
  return 'gold';
}

export interface TierThresholds {
  goldGuesses: number;
  goldTimeMs: number;
  silverGuesses: number;
  giveUpPenalty: number;
}

export const THRESHOLDS: TierThresholds = {
  goldGuesses: TIER_GOLD_GUESSES,
  goldTimeMs: TIER_GOLD_TIME_MS,
  silverGuesses: TIER_SILVER_GUESSES,
  giveUpPenalty: GIVE_UP_PENALTY,
};
